/**
 * Copyright (c) 2021 FMR LLC.
 * All Rights Reserved.
 * Fidelity Confidential Information.
 */

export const ClientConstants = {
    // Heading text
    OPENING: 'To calculate the estimated yield or price of a bond, enter the information below.',
    LEARN_MORE: 'Learn more about this calculator.',
    ADVISEMENT_1: 'You are advised to review the following ',
    TERMS_AND_CONDITIONS: 'Terms and Conditions of Use',
    ADVISEMENT_2: ' before using this calculator.',

    // Footer text
    FOOTER_HEADER_1: 'About Bond Prices and Yields',
    FOOTER_NOTE_1_A: 'The chart above illustrates the relationship between the price and yield of a bond. ' +
        'Prices and yields generally move in opposite directions. If a bond\'s price goes up, its yield goes ' +
        'down. If the bond\'s price falls, the yield rises. Likewise, if the yield increases, the price falls, ' +
        'and vice versa. Use the calculator to see this effect. For a bond\'s yield to maturity (and yield to call, ' +
        'if the bond is callable) enter the bond\'s price.',
    FOOTER_NOTE_1_B: ' The yield to worst would be lower of the two yields.',
    FOOTER_NOTE_1_C: 'For a bond\'s price, enter its yield. ' +
        'Hover over the chart to see specific bond and price information that updates dynamically ' +
        'as the relationship between price and yield changes. Note that hover over capability does ' +
        'not impact the calculations already displaying above the chart. To update the calculations, ' +
        'input new price or yield criteria to the left.',
    FOOTER_HEADER_2: 'Next Step',
    FOOTER_NOTE_2: 'Search Our Bond Offerings',

    // input labels
    CALCULATE_LABEL: 'Calculate',
    BOND_TYPE_LABEL: 'Bond Type',
    PURCHASE_PRICE_LABEL: 'Price',
    YIELD_TO_MATURITY_LABEL: '* Yield to Maturity or Call',
    ANNUAL_COUPON_RATE_LABEL: '# Annual Coupon Rate',
    COUPON_FREQUENCY_LABEL: 'Coupon Frequency',
    MATURITY_DATE_LABEL: 'Maturity Date',
    MATURITY_OR_CALL_DATE_LABEL: 'Maturity or Call Date',
    CALL_DATE_LABEL: 'Call Date (if callable)',
    CALL_PRICE_LABEL: 'Call Price',
    PAR_VALUE_LABEL: 'Par Value',
    QUANTITY_LABEL: 'Quantity',
    SETTLEMENT_DATE_LABEL: 'Settlement Date',
    MARK_UP_LABEL: '* Total Mark-up',

    // result labels
    RESULT_ACCRUED_INTEREST_LABEL: 'Accrued Interest',
    RESULT_TOTAL_COST_LABEL: 'Total Cost',
    RESULT_PRICE_LABEL: 'Price',
    RESULT_YIELD_TO_MATURITY_LABEL: 'Yield to Maturity',
    RESULT_YIELD_TO_CALL_LABEL: 'Yield to Call',

    // chart hover lables
    YIELD_TO_MATURITY_CHART_HOVER: '<b>Price</b>: {point.x:.3f}<br><b>Yield to Maturity</b>: {point.y:.3f}%',
    YIELD_TO_CALL_CHART_HOVER: '<b>Price</b>: {point.x:.3f}<br><b>Yield to Call</b>: {point.y:.3f}%',
    YIELD_CHART_HOVER: '<b>Price</b>: {point.x:.3f}<br><b>Yield</b>: {point.y:.3f}%',

    // legends
    YIELD_TO_MATURITY_LEGEND: 'Yield to Maturity',
    YIELD_TO_CALL_LEGEND: 'Yield to Call',
    YIELD_LEGEND: 'Yield to Maturity or Call',

    // Input field errors
    PURCHASE_PRICE_ERROR: 'Price must be in the range 1 through 200.',
    YIELD_TO_MATURITY_ERROR: 'Yield to Maturity or Yield to Call must be in the range 0 through 25.',
    COUPON_RATE_ERROR: 'Annual Coupon Rate must be in the range 0 through 25.',
    MATURITY_DATE_ERROR: 'Select Maturity Date using the format MM/DD/YYYY.',
    MATURITY_OR_CALL_DATE_ERROR: 'Select Maturity or Call Date using the format MM/DD/YYYY.',
    QUANTITY_ERROR: 'Quantity must be in the range 1 through 1000000.',
    SETTLEMENT_DATE_ERROR: 'Select Settlement Date using the format MM/DD/YYYY.',
    SETTLEMENT_BEFORE_MATURITY_ERROR: 'Settlement Date must be before Maturity Date.',
    CALL_DATE_ERROR: 'Call Date must be between Maturity Date and Settlement Date.',
    CALL_PRICE_ERROR: 'Call Price is not in the range 1 through 200.',
    FEE_ERROR: 'Mark-up is not in the range 0 through 10,000.',
    CALL_PRICE_NOT_PRESENT_ERROR: 'Call Price is required when a Call Date is entered',
    CALL_DATE_NOT_PRESENT_ERROR: 'Call Date is required when a Call Price is entered',

    // Selection footnote
    SELECTION_FOOTNOTE_FOR_YIELD: '* When a Mark-up is entered, the calculated yield(s) will take into account the mark-up entered.',
    SELECTION_FOOTNOTE_FOR_PRICE: '* Please enter either the Yield to Maturity or Yield to Call and the respective Maturity or Call Date.',
    FOOTNOTE_FOR_ANNUAL_COUPON_RATE: '# Yield and Price calculations for zero coupon bonds and US Treasury bills are approximate.',

    // Popover content strings
    ACR_POPOVER_KEY: 'AnnualCouponRate',
    ACR_POPOVER_HEADING: 'Annual Coupon Rate',
    ACR_POPOVER_TEXT: 'The percentage which is multiplied by the principal to arrive at the yearly coupon payment. While many bonds pay ' +
        'interest more often than annually, coupon rates are given in annual percentages.',

    AI_POPOVER_KEY: 'AccruedInterest',
    AI_POPOVER_HEADING: 'Accrued Interest',
    AI_POPOVER_TEXT: 'Accrued Interest is the interest received from a security\'s last coupon payment date, ' +
        'up to but not including the settlement date: Accrued Interest = Principal x Coupon x Elapsed Days / 360 (corp bond)',

    CF_POPOVER_KEY: 'CouponFrequency',
    CF_POPOVER_HEADING: 'Coupon Frequency',
    CF_POPOVER_TEXT: 'How often per year the bond pays interest.',

    PV_POPOVER_KEY: 'ParValue',
    PV_POPOVER_HEADING: 'Par Value',
    PV_POPOVER_TEXT: 'Also known as Face Value, the stated value of an investment at maturity. ' +
        'Typically $1,000 in the case of most bonds.',

    CD_POPOVER_KEY: 'CallDate',
    CD_POPOVER_HEADING: 'Call Date',
    CD_POPOVER_TEXT: 'Date on which a fixed-income security can be redeemed prior to maturity by the issuer at its corresponding call price.',

    CP_POPOVER_KEY: 'CallPrice',
    CP_POPOVER_HEADING: 'Call Price',
    CP_POPOVER_TEXT: 'Price at which a fixed-income security will be redeemed, if and when it is redeemed prior to maturity by the issuer.',

    SD_POPOVER_KEY: 'SettlementDate',
    SD_POPOVER_HEADING: 'Settlement Date',
    SD_POPOVER_TEXT: 'The date on which an executed trade of securities must be paid for.',

    MU_POPOVER_KEY: 'CommissionFee',
    MU_POPOVER_HEADING: 'Total Mark-up',
    MU_POPOVER_TEXT: 'The fee paid to a broker for executing a trade. The amount you enter ' +
        'will be added directly to the Total Cost result.  Enter on the basis of the mark-up ' +
        'per bond multiplied by the quantity of bonds to be traded.  For example, if you are ' +
        'buying ten bonds and the mark-up is $1 per bond, enter $10 here.',

    LM_POPOVER_HEADING: 'About This Calculator',
    LM_BOND_YIELD_URL: 'https://www.fidelity.com/learning-center/investment-products/fixed-income-bonds/bond-prices-rates-yields#yield',
    LM_LEARNING_CENTER_URL: 'https://www.fidelity.com/learning-center/investment-products/fixed-income-bonds/fixed-income-learning-path',

    TC_POPOVER_KEY: 'TermsAndConditions',
    TC_POPOVER_HEADING: 'Terms and Conditions of Use',
    TC_POPOVER_TEXT: 'The Price/Yield Calculator is made available on an as-is, ' +
        'as-available basis. Price/Yield Calculator content is general in ' +
        'nature, is provided for informational purposes only and should ' +
        'not be construed as legal, tax or investment advice. Fidelity ' +
        'cannot guarantee that the content is accurate, complete, or ' +
        'timely, or that the calculator produces accurate and/or ' +
        'complete results. Laws of a particular state or laws which may ' +
        'be applicable to a particular situation may have an impact on ' +
        'the applicability, accuracy, or completeness of information ' +
        'provided. Federal and state laws and regulations are complex and ' +
        'are subject to change. Changes in such laws and regulations may ' +
        'have a material impact on pre- and/or after-tax investment ' +
        'results. Fidelity makes no warranties with regard to, bears no ' +
        'responsibility for, and disclaims any liability arising out of, ' +
        'an investor\'s use (or the results obtained from, interpretations ' +
        'made as a result of, or any tax position taken in reliance on ' +
        'information provided pursuant to, such investor\'s use) of the ' +
        'calculator. An investor should contact an attorney, tax ' +
        'advisor or investment professional regarding the investor\'s ' +
        'specific legal, tax and/or investment situation prior to taking ' +
        'any action based upon the information provided.',

    YTM_POPOVER_KEY: 'YieldToMaturity',
    YTM_POPOVER_HEADING: 'Yield to Maturity',
    YTM_POPOVER_TEXT: 'The internal rate of return of a bond. This calculation takes into account the time value of the investment.',

    YTC_POPOVER_KEY: 'YieldToCall',
    YTC_POPOVER_HEADING: 'Yield to Call',
    YTC_POPOVER_TEXT: 'A yield measure that uses the same calculation technique ' +
        'as the yield to maturity. The yield to call method, however, substitutes ' +
        'the call date for the maturity date. Typically, the yield to call is calculated ' +
        'using the first possible call date. It may, however, be necessary to use some or ' +
        'all of a security\'s later call dates.',
    GLANCE_URL_DEV: 'https://dmt.fidelity.com/dev/meas/',
    GLANCE_URL_QA: 'https://dmt.fidelity.com/stage/meas/',
    GLANCE_URL_PROD: 'https://dmt.fidelity.com/prod/meas/',
    FIDELITY_URL: 'https://www.fidelity.com',
    // Dropdown values
    yieldTypeList: [
        { value: 'YIELD', text: 'Yield' },
        { value: 'PRICE', text: 'Price' },
    ],

    bondTypeList: [
        { value: 'Corporate', text: 'Corporate' },
        { value: 'Municipal', text: 'Municipal' },
        { value: 'Treasury', text: 'Treasury' },
        { value: 'AgencyGSE', text: 'Agency/GSE' },
        { value: 'FixedRate', text: 'Fixed Rate' },
    ],

    couponFrequencyList: [
        { value: '1', text: 'Annual' },
        { value: '2', text: 'Semi-Annual' },
        { value: '3', text: 'Quarterly' },
        { value: '4', text: 'Monthly/GSE' },
    ],

    parValueList: [
        { value: '25.00', text: '$ 25' },
        { value: '50.00', text: '$ 50' },
        { value: '100.00', text: '$ 100' },
        { value: '1000.00', text: '$ 1000' },
    ],
}

// Used by LinkableLabel to select correct heading and text for popover
export const PopoverContent = [
    {
        key: ClientConstants.ACR_POPOVER_KEY,
        heading: ClientConstants.ACR_POPOVER_HEADING,
        text: ClientConstants.ACR_POPOVER_TEXT,
        id: 'acr',
    },
    {
        key: ClientConstants.CF_POPOVER_KEY,
        heading: ClientConstants.CF_POPOVER_HEADING,
        text: ClientConstants.CF_POPOVER_TEXT,
        id: 'cf',
    },
    {
        key: ClientConstants.PV_POPOVER_KEY,
        heading: ClientConstants.PV_POPOVER_HEADING,
        text: ClientConstants.PV_POPOVER_TEXT,
        id: 'pv',
    },
    {
        key: ClientConstants.CP_POPOVER_KEY,
        heading: ClientConstants.CP_POPOVER_HEADING,
        text: ClientConstants.CP_POPOVER_TEXT,
        id: 'cp',
    },
    {
        key: ClientConstants.CD_POPOVER_KEY,
        heading: ClientConstants.CD_POPOVER_HEADING,
        text: ClientConstants.CD_POPOVER_TEXT,
        id: 'cd',
    },
    {
        key: ClientConstants.SD_POPOVER_KEY,
        heading: ClientConstants.SD_POPOVER_HEADING,
        text: ClientConstants.SD_POPOVER_TEXT,
        id: 'sd',
    },
    {
        key: ClientConstants.MU_POPOVER_KEY,
        heading: ClientConstants.MU_POPOVER_HEADING,
        text: ClientConstants.MU_POPOVER_TEXT,
        id: 'mu',
    },
    {
        key: ClientConstants.TC_POPOVER_KEY,
        heading: ClientConstants.TC_POPOVER_HEADING,
        text: ClientConstants.TC_POPOVER_TEXT,
        id: 'tc',
    },
    {
        key: ClientConstants.AI_POPOVER_KEY,
        heading: ClientConstants.AI_POPOVER_HEADING,
        text: ClientConstants.AI_POPOVER_TEXT,
        id: 'ai',
    },
    {
        key: ClientConstants.YTM_POPOVER_KEY,
        heading: ClientConstants.YTM_POPOVER_HEADING,
        text: ClientConstants.YTM_POPOVER_TEXT,
        id: 'ytm',
    },
    {
        key: ClientConstants.YTC_POPOVER_KEY,
        heading: ClientConstants.YTC_POPOVER_HEADING,
        text: ClientConstants.YTC_POPOVER_TEXT,
        id: 'ytc',
    },
]
